import 'choices.js/public/assets/styles/choices.css'
import Choices, { type InputChoice, type Options } from 'choices.js'
import type { ChoiceFull } from 'choices.js/public/types/src/scripts/interfaces/choice-full'
import type { TemplateOptions } from 'choices.js/public/types/src/scripts/interfaces/templates'

export type Select = Choices

const defaultConfig: Partial<Options> = {
  callbackOnCreateTemplates: function (this: Choices, strToEl) {
    const config: Options = this.config

    const passedElement = this.passedElement
    let applyButtonText: null | string = null

    if (passedElement?.element?.hasAttribute('data-apply-button')) {
      applyButtonText = passedElement?.element?.getAttribute('data-apply-button') as string
    }

    return {
      choice: (options: TemplateOptions, choice: ChoiceFull) => {
        const { classNames } = { ...options }
        return strToEl(`
        <div
    class="${Array.isArray(classNames.item) ? classNames.item.join(' ') : classNames.item} ${
      Array.isArray(classNames.itemChoice) ? classNames.itemChoice.join(' ') : classNames.itemChoice
    } ${
      choice.disabled
        ? Array.isArray(classNames.itemDisabled)
          ? classNames.itemDisabled.join(' ')
          : classNames.itemDisabled
        : Array.isArray(classNames.itemSelectable)
          ? classNames.itemSelectable.join(' ')
          : classNames.itemSelectable
    }"
    data-select-text="${config?.itemSelectText}"
    data-choice
    ${choice.value === '' && passedElement?.element?.hasAttribute('required') ? 'data-disabled="true"' : ''}
    data-id="${choice.id.toString()}"
    data-value="${choice.value}"
    data-selected="${choice.selected ? '1' : '0'}"
    ${choice.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}
    id="${choice.elementId as string}"
    ${choice.group && choice.group.id > 0 ? 'role="treeitem"' : 'role="option"'}
    >
    ${choice.label as string}
    </div>
      `) as HTMLDivElement
      },
      dropdown(options: TemplateOptions): HTMLDivElement {
        const { classNames } = { ...options }
        const template = document.createElement('div')

        template.className = `${Array.isArray(classNames.list) ? classNames.list.join(' ') : classNames.list} ${Array.isArray(classNames.listDropdown) ? classNames.listDropdown.join(' ') : classNames.listDropdown}`
        template.setAttribute('aria-expanded', 'false')

        if (typeof applyButtonText !== 'undefined' && applyButtonText !== null) {
          const div = document.createElement('div')
          div.innerHTML = `<div class="choices__list-apply"><span class="dca-button dca-button--secondary-outline">${applyButtonText}</span></div>`
          template.setAttribute('data-with-apply', 'true')
          template.appendChild(div)
        }

        return template
      },
    }
  },
  searchEnabled: false,
  searchChoices: false,
  shouldSort: false,
  resetScrollPosition: false,
}

const defaultMultipleConfig = {
  renderSelectedChoices: 'always',
}

const selects: Record<string, Select> = {}

export default {
  initSelect(el: HTMLSelectElement, config?: Partial<Options>) {
    if (!el) {
      return
    }

    if (!el.getAttribute('data-choice')) {
      let id = el.getAttribute('id')

      if (!id) {
        do {
          id = Math.random().toString(36).substr(2, 9)
        } while (typeof selects[id] !== 'undefined')

        el.setAttribute('id', id)
      }

      let choicesConfig = Object.assign({}, defaultConfig, config)

      if (el.multiple === true) {
        choicesConfig = Object.assign(choicesConfig, defaultMultipleConfig)
      }

      selects[id] = new Choices(el, choicesConfig)
      const currentSelect = selects[id]
      if (el.hasAttribute('data-apply-button')) {
        currentSelect?.dropdown?.element?.querySelector('.choices__list-apply')?.addEventListener('click', function () {
          currentSelect.hideDropdown()
        })
      }

      // resynchronise the select with the value being sent by the library
      el.addEventListener(
        'change',
        (e: Event) => {
          const event = e as CustomEvent<{ value: string }>
          const eventDetail = event.detail

          if (eventDetail) {
            el.value = eventDetail.value
          }
        },
        true
      )

      // fix native select from opening when clicking on a label that describes it
      document.querySelectorAll(`label[for="${id}"]`).forEach((labelEl) => {
        labelEl.addEventListener('click', function (e) {
          e.preventDefault()
          currentSelect?.showDropdown()
        })
      })

      if (el.multiple === true && selects[id] && selects[id]?.input) {
        // @ts-expect-error object is not undefined
        selects[id].input.placeholder = el.getAttribute('data-placeholder') as string
        // @ts-expect-error object is not undefined
        selects[id].input.element.readOnly = true
      }

      // fix ie11 issue https://github.com/jshjohnson/Choices/issues/834
      if (!selects[id]?.dropdown.element.classList.contains('choices__list--dropdown')) {
        selects[id]?.dropdown.element.classList.add('choices__list--dropdown')
      }

      // fix chrome bug where the scrollbar space will still appear even if the scrollbar itself is not displayed
      currentSelect?.passedElement.element.addEventListener('showDropdown', function (this: Element) {
        // @ts-expect-error object is not null
        this.closest('.choices')
          .querySelectorAll('.choices__list .choices__item')
          .forEach(function (el: Element) {
            el.classList.add('choices__item--reflow')

            window.setTimeout(() => {
              el.classList.remove('choices__item--reflow')
            }, 0)
          })
      })

      // fix ie11 bug when selecting an option would retrigger the html validation
      currentSelect?.passedElement.element.addEventListener('choice', function (this: HTMLSelectElement, e) {
        if (this.hasAttribute('required')) {
          this.removeAttribute('required')
          this.setAttribute('required', 'required')
          // @ts-expect-error no real error
          this.value = e.detail?.choice?.value
        }
      })

      if (el.multiple === true) {
        currentSelect?.passedElement.element.addEventListener('choice', function (this: Element, e) {
          // @ts-expect-error property exists
          const detail: { choice: InputChoice } = e.detail

          if (detail && detail.choice.disabled === false && detail.choice.selected === true) {
            setTimeout(() => {
              currentSelect.removeActiveItemsByValue(detail.choice.value as string)
              el.dispatchEvent(
                new CustomEvent('change', {
                  bubbles: true,
                })
              )
            })
          }
        })
        if ([...el.options].filter((x) => x.selected).length > 0) {
          el.parentNode?.querySelector('input.choices__input')?.classList.add('choices__input--no-placeholder')
        }
        el.addEventListener('change', function () {
          if (el.children.length === 0) {
            el.parentNode?.querySelector('input.choices__input')?.classList.remove('choices__input--no-placeholder')
          } else {
            el.parentNode?.querySelector('input.choices__input')?.classList.add('choices__input--no-placeholder')
          }
        })
      }

      return currentSelect
    }

    return false
  },

  getSelect(id: string, initialize = true) {
    if (!selects[id] && initialize) {
      this.initSelect(document.getElementById(id) as HTMLSelectElement)
    }

    return selects[id] as Select
  },

  getValue(id: string): string | Array<string> {
    if (!id) {
      return ''
    }

    if (!selects[id]) {
      this.initSelect(document.getElementById(id) as HTMLSelectElement)
    }

    return selects[id]?.getValue(true) as string | Array<string>
  },

  setValue(id: string, value: string) {
    if (!id || typeof value === 'undefined') {
      return false
    }

    if (!selects[id]) {
      this.initSelect(document.getElementById(id) as HTMLSelectElement)
    }

    selects[id]?.setChoiceByValue(value)
    selects[id]?.passedElement.element.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          src: 'setValue',
          value: selects[id]?.passedElement.element.value,
        },
      })
    )

    return true
  },

  removeValue(id: string, value: string): boolean {
    if (!id || typeof value === 'undefined' || !selects[id]) {
      return false
    }

    selects[id]?.removeActiveItemsByValue(value)
    selects[id]?.passedElement.element.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          src: 'setValue',
          value: selects[id]?.passedElement.element.value,
        },
      })
    )

    return true
  },

  removeValues(id: string): boolean {
    if (!id || !selects[id]) {
      return false
    }

    selects[id]?.removeActiveItems()
    selects[id]?.passedElement.element.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          src: 'setValue',
          value: selects[id]?.passedElement.element.value,
        },
      })
    )

    return true
  },

  /*
   * values:
    {
      choices: {
        value1: "label1",
        value2: "label2"
      },
      value: value1 // the selected value
    }
  */
  async setValues(id: string, values: { choices: Record<string, string>; value: string }): Promise<void> {
    const formattedValues: {
      value: string
      label: string
      selected: boolean
    }[] = []
    for (const value in values.choices) {
      formattedValues.push({
        value: value.toString(),
        label: values.choices[value] as string,
        selected: values.value ? values.value.toString() === value.toString() : false,
      })
    }

    await selects[id]?.setChoices(formattedValues, 'value', 'label', true)

    if (values.value) {
      selects[id]?.setChoiceByValue(values.value)
      selects[id]?.passedElement.element.dispatchEvent(
        new CustomEvent('change', {
          detail: {
            src: 'setValue',
            value: selects[id]?.passedElement.element.value,
          },
        })
      )
    }
  },

  clear(id: string): boolean {
    if (!id || !selects[id]) {
      return false
    }

    selects[id]?.clearChoices()

    return true
  },

  async clearSelection(id: string): Promise<void> {
    if (selects[id]?.passedElement.element.multiple === false) {
      selects[id]?.setChoiceByValue('0')
    } else {
      const initialChoices: {
        value: string
        label: string
        disabled: boolean
      }[] = []
      selects[id]?.passedElement?.element?.parentNode?.parentNode?.querySelectorAll('.choices__list--dropdown .choices__item').forEach(function (el) {
        initialChoices.push({
          value: el.getAttribute('data-value') as string,
          label: el.innerHTML,
          disabled: el.getAttribute('aria-disabled') !== null,
        })
      })

      selects[id]?.destroy()
      selects[id]?.init()
      await selects[id]?.setChoices(initialChoices, 'value', 'label', true)
    }
  },
}
